import { ref, computed, unref, watch } from 'vue';

/**
 * Detects that a target element's visibility.
 *
 * @see https://vueuse.org/useIntersectionObserver
 * @param target
 * @param callback
 * @param options
 */
export const useIntersectionObserver = (
    target,
    callback,
    options = {},
  ) => {
    const {
      root,
      rootMargin = '0px',
      threshold = 0.1,
      immediate = true,
    } = options;

    const isSupported = ref(true);
    const targets = computed(() => {
      const _target = unref(target);
      return (Array.isArray(_target) ? _target : [_target]).map(unref).filter((item) => !!item);
    });

    let cleanup = () => {};
    const isActive = ref(immediate)

    const stopWatch = isSupported.value
      ? watch(
        () => [targets.value, unref(root), isActive.value],
        ([targets, root]) => {
          cleanup()
          if (!isActive.value)
            return

          if (!targets.length)
            return

          const observer = new IntersectionObserver(
            callback,
            {
              root: unref(root),
              rootMargin,
              threshold,
            },
          )

          targets.forEach(el => el && observer.observe(el))

          cleanup = () => {
            observer.disconnect()
            cleanup = () => {}
          }
        },
        { immediate, flush: 'post' },
      )
      : () => {}

    const stop = () => {
      cleanup()
      stopWatch()
      isActive.value = false
    }

    return {
      isSupported,
      isActive,
      pause() {
        cleanup()
        isActive.value = false
      },
      resume() {
        isActive.value = true
      },
      stop,
    }
  }
